import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useHasPermission } from '../../../hooks/useHasPermission';

enum OrderPermissions {
    techOrderAdmin = 'TECH_ORDER_ADMIN',
    updateOrder = 'UPDATE_ORDER',
    updateDuty = 'UPDATE_DUTY',
}

interface PermissionsContextType {
    hasUpdateOrderPermission: boolean;
    hasUpdateDutyPermission: boolean;
    hasOrderAdminPermission: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | null>(null);

const usePermissions = (): PermissionsContextType => {
    const context = useContext(PermissionsContext);
    if (!context)
        throw new Error(
            'usePermissions must be used within PermissionsProvider'
        );
    return context;
};

const PermissionsProvider = ({ children }: PropsWithChildren): ReactElement => {
    const hasUpdateOrderPermission = useHasPermission(
        OrderPermissions.updateOrder
    );
    const hasUpdateDutyPermission = useHasPermission(
        OrderPermissions.updateDuty
    );
    const hasOrderAdminPermission = useHasPermission(
        OrderPermissions.techOrderAdmin
    );

    const contextValue: PermissionsContextType = useMemo(
        () => ({
            hasUpdateOrderPermission,
            hasUpdateDutyPermission,
            hasOrderAdminPermission,
        }),
        [
            hasUpdateDutyPermission,
            hasUpdateOrderPermission,
            hasOrderAdminPermission,
        ]
    );

    return (
        <PermissionsContext.Provider value={contextValue}>
            {children}
        </PermissionsContext.Provider>
    );
};

export { PermissionsProvider, usePermissions };
